import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getCurrentSession, getLoginUrl } from './auth';
import { signUserInfo, extractUserInfoFromSession } from '../utils/jwt';

const baseURL = process.env.REACT_APP_KFC_API || 'http://localhost:30080/api/kfc';
// JWT密钥，用于签名用户信息（应该由auth服务颁发给kfc-web服务）
const JWT_SECRET = process.env.REACT_APP_JWT_SECRET || 'kfc-web-secret-key-change-in-production';

export const kfcApi = axios.create({ baseURL, timeout: 10000 });

// 请求拦截器：校验会话并添加用户信息签名
kfcApi.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    let session = null;
    try {
      session = await getCurrentSession();
    } catch (error) {
      // whoami 发生网络/CORS异常时，不跳转登录，避免 30090 <-> 3001 循环。
      return Promise.reject(error);
    }

    if (!session) {
      // 仅当确实无会话时跳转登录（此时不会发出 KFC API 请求，只会重定向）
      const loginUrl = getLoginUrl();
      const returnUrl = encodeURIComponent(window.location.href);
      if (process.env.NODE_ENV === 'development') {
        console.warn('[KFC API] 无会话，将跳转登录。请先通过 Kratos 登录后再访问。', { loginUrl });
      }
      window.location.href = `${loginUrl}?returnUrl=${returnUrl}`;
      return Promise.reject(new Error('未登录，正在跳转到登录页面'));
    }

    try {
      const userInfo = extractUserInfoFromSession(session);
      const signedToken = await signUserInfo(userInfo, JWT_SECRET);
      config.headers['X-User-Info'] = signedToken;
      return config;
    } catch (error: any) {
      // 有 session 但提取/签名失败时不跳转登录，仅让本次请求失败
      return Promise.reject(error);
    }
  },
  (error) => Promise.reject(error)
);

// 响应拦截器：仅当会话确实失效（whoami 无会话）时才跳转登录，避免 KFC 后端 401 误判为未登录
kfcApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      let session = null;
      try {
        session = await getCurrentSession();
      } catch {
        // whoami 非 401 场景（如 CORS/网络）不触发重定向，避免循环跳转
        return Promise.reject(error);
      }
      if (!session) {
        const loginUrl = getLoginUrl();
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.href = `${loginUrl}?returnUrl=${returnUrl}`;
      }
    }
    return Promise.reject(error);
  }
);

/** 与后端 AgentBasicInfo / collection 结构一致 */
export interface AgentDTO {
  id?: string;
  basicInfo?: {
    name?: string;
    identifier?: string;
  };
  contact?: {
    contactPerson?: { name?: string; phone?: string };
    address?: string;
  };
  officialWebsite?: {
    url?: string;
    username?: string;
    password?: string;
  };
  apiConfig?: {
    priceEndPoint?: string;
    trackEndPoint?: string;
    logisticsSystemEndPoint?: string;
    appKey?: string;
    appSecret?: string;
  };
  bankAccount?: {
    bankName?: string;
    accountName?: string;
    accountNumber?: string;
  };
  /** 代理能力映射：能力名称 -> 对应 API 名称 */
  capabilities?: { capability?: string; apiName?: string }[];
  createdAt?: string;
}

export interface ChannelDTO {
  id: string;
  agentId: string;
  channelCode: string;
  channelName?: string;
  carrier?: string;
  weightRuleJson?: string;
  versionTimestamp?: string;
}

/** 起运地/目的地：国家名称、国家编码(ISO 3166-1 alpha-2)、城市名称 */
export interface RouteLocationDTO {
  countryName?: string;
  countryCode?: string;
  cityName?: string;
}

export interface RouteDTO {
  id: string;
  routeCode: string;
  origin?: RouteLocationDTO;
  destination?: RouteLocationDTO;
  transitTime?: string;
  transportMode?: string;
  customsProps?: string;
  priceCoefficient?: number;
  isActive?: boolean;
  channelIds?: string[];
}

export interface ReceivingRecordDTO {
  id: string;
  trackingNumber: string;
  source?: string;
  supplier?: string;
  preDeclarationId?: string;
  shipmentId?: string;
  measurementJson?: string;
  trackingDetailsJson?: string;
  createdAt?: string;
}

export interface PreDeclarationDTO {
  id: string;
  preDeclarationCode: string;
  clientCode: string;
  clientAddr?: string;
  routeId: string;
  status: string;
  expectedShipDate?: string;
  cargoDescJson?: string;
  customsDeclJson?: string;
  shipmentId?: string;
  createdBy?: string;
  createdAt?: string;
  receivingRecords?: ReceivingRecordDTO[];
}

export interface ShipmentInvoiceDTO {
  id: string;
  shipmentId: string;
  fileName: string;
  generatedAt: string;
}

export interface ShipmentDTO {
  shipmentId: string;
  /** 预申报单 ID，用于绑定货物（仅预申报状态时有值） */
  preDeclarationId?: string;
  clientCode?: string;
  clientAddr?: string;
  routeId?: string;
  routeName?: string;
  channelShipmentId?: string;
  /** 代理入库单号（交代理仓后由代理 API 返回） */
  agentInboundNo?: string;
  /** 代理入库状态（0新增,1已下单,2收货中,3已收货,5已确认,7调拨中,8扣货等） */
  agentInboundStatus?: number;
  /** 代理出库状态（0待出库,1出库中,2已出库） */
  agentOutboundStatus?: number;
  /** 代理测量数据（JSON 字符串） */
  agentMeasurementDataJson?: string;
  /** 最新轨迹展示（物流格式、按时间排序） */
  agentTrackingLatest?: { time?: string; location?: string; description?: string }[];
  status: string;
  expectedShipDate?: string;
  cargoDescJson?: string;
  customsDeclJson?: string;
  confirmedWeight?: number;
  pendingWeight?: number;
  createdAt?: string;
  updatedAt?: string;
  receivingRecords?: ReceivingRecordDTO[];
}

export const agentApi = {
  list: () => kfcApi.get('/agents') as Promise<AxiosResponse<AgentDTO[]>>,
  get: (id: string) => kfcApi.get(`/agents/${id}`) as Promise<AxiosResponse<AgentDTO>>,
  create: (dto: Partial<AgentDTO>) => kfcApi.post('/agents', dto) as Promise<AxiosResponse<AgentDTO>>,
  update: (id: string, dto: Partial<AgentDTO>) => kfcApi.put(`/agents/${id}`, dto) as Promise<AxiosResponse<AgentDTO>>,
  delete: (id: string) => kfcApi.delete(`/agents/${id}`),
  testConnection: (id: string) => kfcApi.post(`/agents/${id}/test-connection`) as Promise<AxiosResponse<{ success: boolean }>>,
};

export const channelApi = {
  list: (agentId?: string) => kfcApi.get('/channels', { params: agentId ? { agentId } : {} }) as Promise<AxiosResponse<ChannelDTO[]>>,
  get: (id: string) => kfcApi.get(`/channels/${id}`) as Promise<AxiosResponse<ChannelDTO>>,
  save: (dto: Partial<ChannelDTO>) => kfcApi.post('/channels', dto) as Promise<AxiosResponse<ChannelDTO>>,
  update: (id: string, dto: Partial<ChannelDTO>) => kfcApi.put(`/channels/${id}`, dto) as Promise<AxiosResponse<ChannelDTO>>,
  delete: (id: string) => kfcApi.delete(`/channels/${id}`),
};

export const routeApi = {
  list: (activeOnly?: boolean) => kfcApi.get('/routes', { params: activeOnly ? { activeOnly: true } : {} }) as Promise<AxiosResponse<RouteDTO[]>>,
  get: (id: string) => kfcApi.get(`/routes/${id}`) as Promise<AxiosResponse<RouteDTO>>,
  create: (dto: Partial<RouteDTO>) => kfcApi.post('/routes', dto) as Promise<AxiosResponse<RouteDTO>>,
  update: (id: string, dto: Partial<RouteDTO>) => kfcApi.put(`/routes/${id}`, dto) as Promise<AxiosResponse<RouteDTO>>,
};

export const preDeclarationApi = {
  list: () => kfcApi.get('/pre-declarations') as Promise<AxiosResponse<PreDeclarationDTO[]>>,
  get: (id: string) => kfcApi.get(`/pre-declarations/${id}`) as Promise<AxiosResponse<PreDeclarationDTO>>,
  create: (dto: { clientCode: string; clientAddr?: string; routeCode: string; expectedShipDate?: string; cargoDescJson: string; customsDeclJson?: string }) =>
    kfcApi.post('/pre-declarations', dto) as Promise<AxiosResponse<PreDeclarationDTO>>,
  bindReceiving: (id: string, receivingRecordIds: string[]) =>
    kfcApi.post(`/pre-declarations/${id}/bind-receiving`, { preDeclarationId: id, receivingRecordIds }),
};

export const shipmentApi = {
  list: (params?: { clientCode?: string; status?: string; page?: number; size?: number }) =>
    kfcApi.get('/shipments', { params: params || {} }) as Promise<AxiosResponse<{ content: ShipmentDTO[]; totalElements: number }>>,
  get: (shipmentId: string) => kfcApi.get(`/shipments/${shipmentId}`) as Promise<AxiosResponse<ShipmentDTO>>,
  handoverConfirm: (dto: { shipmentId: string; pendingWeight?: number; handoverProofJson?: string; deliveryTrackingNo?: string }) =>
    kfcApi.post('/shipments/handover', dto),
  weightConfirm: (dto: { shipmentId: string; confirmedWeight?: number; accept?: boolean }) =>
    kfcApi.post('/shipments/weight-confirm', dto),
  setChannelShipmentId: (shipmentId: string, channelShipmentId: string) =>
    kfcApi.put(`/shipments/${shipmentId}/channel-shipment-id`, { channelShipmentId }),
  getSubmitDepotContext: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/submit-depot-context`) as Promise<AxiosResponse<SubmitDepotContextDTO>>,
  submitToAgentDepot: (shipmentId: string, body?: SubmitToDepotRequest) =>
    kfcApi.post(`/shipments/${shipmentId}/submit-depot`, body || {}),
  submitInvoice: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/submit-invoice`),
  /** 生成发票 Excel，返回发票记录 */
  generateInvoice: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/generate-invoice`) as Promise<AxiosResponse<ShipmentInvoiceDTO>>,
  /** 运单下的发票记录列表 */
  listInvoices: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/invoices`) as Promise<AxiosResponse<ShipmentInvoiceDTO[]>>,
  /** 下载发票文件（返回 blob） */
  downloadInvoice: (shipmentId: string, invoiceId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/invoices/${invoiceId}/download`, { responseType: 'blob' }) as Promise<AxiosResponse<Blob>>,
  syncTracking: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/sync-tracking`),
  /** 同步代理入仓数据（入库/出库/测量，仅当有代理入库单号时可操作） */
  syncAgentData: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/sync-agent-data`),
  /** 同步轨迹数据（从代理拉取轨迹，运单已交代理仓及之后可操作） */
  syncTrackingData: (shipmentId: string) =>
    kfcApi.post(`/shipments/${shipmentId}/sync-tracking-data`),
  /** 获取轨迹展示数据（代理原始或兼容结构），用于时间线等富展示 */
  getTrackingDisplay: (shipmentId: string) =>
    kfcApi.get(`/shipments/${shipmentId}/tracking-display`) as Promise<AxiosResponse<TrackingDisplayDTO>>,
};

/** 轨迹展示数据结构（兼容不同代理：56yorky 等含 trackInfoList/inputChanges） */
export interface TrackingDisplayDTO {
  serviceName?: string;
  userNo?: string;
  latestTime?: string;
  latestStatus?: string;
  latestDetail?: string;
  trackInfoList?: TrackInfoItemDTO[];
  inputChanges?: InputChangeItemDTO[];
}

export interface TrackInfoItemDTO {
  trackTime?: string;
  trackStation?: string;
  trackStationEn?: string;
  trackDesc?: string;
  trackDescEn?: string;
  trackStatusText?: string;
  [key: string]: unknown;
}

export interface InputChangeItemDTO {
  createTime?: string;
  busiCode?: string;
  remark?: string;
  [key: string]: unknown;
}

export const receivingRecordApi = {
  listUnbound: () => kfcApi.get('/receiving-records/unbound') as Promise<AxiosResponse<ReceivingRecordDTO[]>>,
  listByPreDeclaration: (preDeclarationId: string) =>
    kfcApi.get(`/receiving-records/by-pre-declaration/${preDeclarationId}`) as Promise<AxiosResponse<ReceivingRecordDTO[]>>,
  create: (dto: { trackingNumber: string; source?: string; supplier?: string; measurementJson?: string }) =>
    kfcApi.post('/receiving-records', dto) as Promise<AxiosResponse<ReceivingRecordDTO>>,
};

export const channelSyncApi = {
  syncFromAgent: (agentId: string) =>
    kfcApi.post(`/channel-sync/agents/${agentId}`) as Promise<AxiosResponse<{ success: boolean; message: string; channels: ChannelDTO[] }>>,
};

export interface AgentDepotDTO {
  id?: string;
  agentId?: string;
  depotId?: string;
  depotName?: string;
  depotAddr?: string;
  depotContact?: string;
}

/** 交代理仓请求：选择仓库、货拉拉单号、照片等 */
export interface SubmitToDepotRequest {
  depotId: string;
  huolalaOrderNo?: string;
  photoUrls?: string[];
}

/** 交代理仓弹窗上下文：代理信息 + 代理仓库列表 */
export interface SubmitDepotContextDTO {
  agentId: string;
  agent: AgentDTO | null;
  depots: AgentDepotDTO[];
}

export interface AgentServiceChannelDTO {
  id?: string;
  agentId?: string;
  serviceId?: number;
  serviceName?: string;
  serviceStatus?: number;
  country?: string;
  stype?: string;
}

export const agentDepotApi = {
  list: (agentId?: string) =>
    kfcApi.get('/agent-depots', { params: agentId ? { agentId } : {} }) as Promise<AxiosResponse<AgentDepotDTO[]>>,
  syncFromAgent: (agentId: string) =>
    kfcApi.post(`/agent-depots/sync/${agentId}`) as Promise<AxiosResponse<{ success: boolean; message: string; depots: AgentDepotDTO[] }>>,
};

export const agentServiceApi = {
  list: (agentId?: string) =>
    kfcApi.get('/agent-services', { params: agentId ? { agentId } : {} }) as Promise<AxiosResponse<AgentServiceChannelDTO[]>>,
  syncFromAgent: (agentId: string) =>
    kfcApi.post(`/agent-services/sync/${agentId}`) as Promise<AxiosResponse<{ success: boolean; message: string; services: AgentServiceChannelDTO[] }>>,
};

export interface CustomerAddressDTO {
  id?: string;
  contactName?: string;
  phone?: string;
  address?: string;
  postalCode?: string;
}

export interface CustomerAccountDTO {
  id?: string;
  transactionJson?: string;
  payableAmount?: number;
  paidAmount?: number;
}

export interface CustomerDTO {
  id?: string;
  customerCode: string;
  domain?: string;
  name: string;
  email?: string;
  phone?: string;
  createdAt?: string;
  updatedAt?: string;
  addresses?: CustomerAddressDTO[];
  account?: CustomerAccountDTO;
}

export const customerApi = {
  list: () => kfcApi.get('/customers') as Promise<AxiosResponse<CustomerDTO[]>>,
  get: (id: string) => kfcApi.get(`/customers/${id}`) as Promise<AxiosResponse<CustomerDTO>>,
  getByCode: (customerCode: string) =>
    kfcApi.get(`/customers/by-code/${encodeURIComponent(customerCode)}`) as Promise<AxiosResponse<CustomerDTO>>,
  create: (dto: Partial<CustomerDTO>) => kfcApi.post('/customers', dto) as Promise<AxiosResponse<CustomerDTO>>,
  update: (id: string, dto: Partial<CustomerDTO>) =>
    kfcApi.put(`/customers/${id}`, dto) as Promise<AxiosResponse<CustomerDTO>>,
  delete: (id: string) => kfcApi.delete(`/customers/${id}`),
};

/** 供应商（履行中心供应商管理） */
export interface SupplierDTO {
  id?: string;
  supplierId?: string;
  supplierName?: string;
  contact?: string;
  phoneNumber?: string;
  address?: string;
  createdAt?: string;
}

export const supplierApi = {
  list: () => kfcApi.get('/suppliers') as Promise<AxiosResponse<SupplierDTO[]>>,
  get: (id: string) => kfcApi.get(`/suppliers/${id}`) as Promise<AxiosResponse<SupplierDTO>>,
  create: (dto: Partial<SupplierDTO>) => kfcApi.post('/suppliers', dto) as Promise<AxiosResponse<SupplierDTO>>,
  update: (id: string, dto: Partial<SupplierDTO>) =>
    kfcApi.put(`/suppliers/${id}`, dto) as Promise<AxiosResponse<SupplierDTO>>,
  delete: (id: string) => kfcApi.delete(`/suppliers/${id}`),
};

/** 产品（设计文档 Product：海关编码、名称、品牌、图片、用途、材质、价格） */
export interface ProductDTO {
  id?: string;
  hscode?: string;
  nameZN?: string;
  nameEN?: string;
  brand?: string;
  picture?: string;
  usage?: { zh_CN?: string; en_US?: string };
  material?: { zh_CN?: string; en_US?: string };
  price?: { USD?: number; RMB?: number };
}

export const productApi = {
  list: () => kfcApi.get('/products') as Promise<AxiosResponse<ProductDTO[]>>,
  get: (id: string) => kfcApi.get(`/products/${id}`) as Promise<AxiosResponse<ProductDTO>>,
  getByHscode: (hscode: string) =>
    kfcApi.get(`/products/by-hscode/${encodeURIComponent(hscode)}`) as Promise<AxiosResponse<ProductDTO>>,
  create: (dto: Partial<ProductDTO>) => kfcApi.post('/products', dto) as Promise<AxiosResponse<ProductDTO>>,
  update: (id: string, dto: Partial<ProductDTO>) =>
    kfcApi.put(`/products/${id}`, dto) as Promise<AxiosResponse<ProductDTO>>,
  delete: (id: string) => kfcApi.delete(`/products/${id}`),
};

/** 货物（设计文档 Carton） */
export interface CartonProductItemDTO {
  hscode?: string;
  amount?: number;
}

export interface CartonDTO {
  id?: string;
  cartonId?: string;
  fromSupplierId?: string;
  belongClientId?: string;
  products?: CartonProductItemDTO[];
  state?: string;
  states?: Record<string, Record<string, unknown>>;
  shipmentId?: string;
  createTimestamp?: number;
}

export interface CreateCartonRequest {
  cartonId: string;
  fromSupplierId?: string;
  belongClientId?: string;
  products: CartonProductItemDTO[];
}

/** 更新货物请求（详情编辑） */
export interface UpdateCartonRequest {
  cartonId?: string;
  fromSupplierId?: string;
  belongClientId?: string;
  shipmentId?: string;
  products?: CartonProductItemDTO[];
}

/** 收货请求：到达方式、查询单号、照片（标注 -> base64） */
export interface ReceiveCartonRequest {
  arrivedBy: number; // 0 货拉拉，1 快递
  queryParamOfShippment?: string;
  pictures?: Record<string, string>;
}

/** 批量收货请求 */
export interface ReceiveCartonBatchRequest {
  cartonIds: string[];
  arrivedBy: number;
  queryParamOfShippment?: string;
  pictures?: Record<string, string>;
}

export const cartonApi = {
  list: () => kfcApi.get('/cartons') as Promise<AxiosResponse<CartonDTO[]>>,
  get: (id: string) => kfcApi.get(`/cartons/${id}`) as Promise<AxiosResponse<CartonDTO>>,
  createCartons: (body: { cartons: CreateCartonRequest[] }) =>
    kfcApi.post('/cartons', body) as Promise<AxiosResponse<CartonDTO[]>>,
  update: (id: string, body: UpdateCartonRequest) =>
    kfcApi.put(`/cartons/${id}`, body) as Promise<AxiosResponse<CartonDTO>>,
  receive: (id: string, body: ReceiveCartonRequest) =>
    kfcApi.post(`/cartons/${id}/receive`, body) as Promise<AxiosResponse<CartonDTO>>,
  receiveBatch: (body: ReceiveCartonBatchRequest) =>
    kfcApi.post('/cartons/receive', body) as Promise<AxiosResponse<CartonDTO[]>>,
};
